import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import {
  FormImage,
  FormInput,
  FormSection,
  FormSelect,
  FormSwitch,
  MultiDocumentSelect,
  Toggle,
} from "../../../Components/Form";
import { formatDateOnly, formatDateTimeLocal, formattedDate, ToastMessage, toOptions, useDebouncedCallback } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { lab_report_attachments_url, user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
import { Loader2, SearchIcon } from "lucide-react";
import { useFetchPatientByTrackingMutation, useFetchPhysiciansAllQuery } from "../../../stores/api/patient.api";
import { PatientSearchHelper } from "../../../stores/api/helpers/patients.helper";
import { patientDetailsSchema } from "./Components/Schemas";
import { PatientProfile } from "../../../Components/PatientProfile";
import labReportHelper from "../../../stores/api/helpers/lab-report.helper";
import { useCreatePatientVisitMutation, useupdateLabReportMutation } from "../../../stores/api/patient.visit.api";
import { useFetchAllDoctorsQuery } from "../../../stores/api/users.api";
import { useFetchAllLabTestsQuery } from "../../../stores/api/lab-test.api";
import { useCreateLabReportMutation, useUpdateLabReportMutation } from "../../../stores/api/lab-report.api";

const CreateLabReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientLabReportDetails = location?.state?.lab_report;

  const [createLabReport, { isLoading: creating }] = useCreateLabReportMutation();
  const [updateLabReport, { isLoading: updating }] = useUpdateLabReportMutation();
  const [searchPatient] = useFetchPatientByTrackingMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    patient_id: patientLabReportDetails?.patient_id || "",
    remarks: patientLabReportDetails?.remarks || "",
    report_type_id: patientLabReportDetails?.report_type_id || "",
    attachments: patientLabReportDetails?.attachments || [],
    status: patientLabReportDetails?.status ? true : false,
  });
  const [searching, setSearching] = useState(false);
  const [patientDetails, setPatientDetails] = useState(patientDetailsSchema)
  
  const physicians = [];

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
  useEffect(() => {
    if (patientLabReportDetails) {
      const tracking_number = patientLabReportDetails?.patient?.system_number || patientLabReportDetails?.patient?.patient_cnic || patientLabReportDetails?.patient?.prescription_pad_no;
      setField("tracking_number", tracking_number)
      handlePatientSearch(tracking_number);
    }
  }, [])
 
  const handlePatientSearch = async (tracking_number = null) => {
    setSearching(true);
    try {
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            searchPatient(
              PatientSearchHelper.forCreate({
                tracking_number: formData.tracking_number || tracking_number || "",
              })
            ).unwrap()
          );
        }, 300);
      });
      const patient = result?.patient;
      setField("patient_id", patient?.id)
      setPatientDetails({
        patient_name: patient?.patient_name || "",
        father_husband_name: patient?.father_husband_name || "",
        patient_type: patient?.patient_type || "",
        gender: patient?.gender || "",
        system_number: patient?.system_number || "",
        dob: patient?.dob || "",
        age: patient?.age || "",
        mobile: patient?.mobile || "",
        patient_cnic: patient?.patient_cnic || "",
        unit_name: patient?.unit?.name || "",
        district_name: patient?.unit?.district?.name || "",
      });
    } catch (error) {
      handleError(error, "Something went wrong.");
    } finally {
      setSearching(false);
    }
  };
  const resetForm = () =>
    setFormData({
      patient_id: "",
      remarks: "",
      report_type_id: "",
      attachments: [],
      status: "",
    });
  const { data: allLabReportTypesRow = [] } =
    useFetchAllLabTestsQuery();
  const allLabReportTypes = useMemo(
    () =>
      (allLabReportTypesRow || []).map((r) => ({
        label: r.name,
        value: r.id,
      })),
    [allLabReportTypesRow],
  );
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (patientLabReportDetails) {
        await updateLabReport(
          labReportHelper.forUpdate(patientLabReportDetails.id, formData),
        ).unwrap();
      } else {
        console.log(formData)
        await createLabReport(labReportHelper.forCreate(formData)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Operation completed successfully.",
        message: patientLabReportDetails
          ? "Lab reports updated successfully!"
          : "Lab reports created successfully!",
      });
      if (!patientLabReportDetails) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };
  const [attachments, setAttachments] = useState(patientLabReportDetails?.attachments || []);

  // Lab Report Attachments
  const handleAttachmentsChange = (
    nextFiles,
    removedFile = null
  ) => {

    if (removedFile?.attachment_file) {
      const updatedExistingAttachments = nextFiles.filter(
        (item) => item?.attachment_file
      );

      setField(
        "existing_attachments",
        JSON.stringify(updatedExistingAttachments)
      );
    }

    const prepared = (nextFiles || []).map((file) => {

      if (file?.attachment_file) {
        return file;
      }

      if (file?.file) {
        return file;
      }

      return {
        name: file.name,
        file,
        preview: file.type?.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };
    });

    setAttachments(prepared);

    setField(
      "attachments",
      prepared
        .filter((item) => item?.file)
        .map((item) => item.file)
    );

    setField(
      "existing_attachments",
      JSON.stringify(
        prepared.filter((item) => item?.attachment_file)
      )
    );
  };
  const patientStatus = formData?.status || "OPD";

  return (
    <div className="p-4">
      <BreadCrumb title={patientLabReportDetails ? "Edit Patient Lab Reports" : "Create Patient Lab Reports"} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <form className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
            <FormSection title="Patient Lab Reports">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <FormSection className="pt-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Patient <span className="text-red-500">*</span>
                    </label>

                    <div className="flex overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-green-500">
                      <input
                        type="text"
                        placeholder="System Number, Prescription Pad No, or CNIC"
                        value={formData.tracking_number}
                        onChange={(e) => setField("tracking_number", e.target.value)}
                        className="flex-1 px-3 py-[8px] outline-none border-none"
                      />
                      <button
                        type="button"
                        onClick={handlePatientSearch}
                        disabled={searching}
                        className="px-5 bg-green-600 text-white hover:bg-green-700 transition"
                      >
                        {searching ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <SearchIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                      Search using the patient's System Number, Prescription Pad Number, or CNIC.
                    </p>
                  </div>
                </FormSection>

                <FormSection title="" className="pt-3">
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                    <FormSelect
                      label="Report Type"
                      isRequired
                      options={allLabReportTypes}
                      value={formData?.report_type_id}
                      onChange={(val) => setField("report_type_id", val ?? "")}
                      blurInputOnSelect
                    />
                  </div>
                </FormSection>
                <FormSection title="" className="pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <FormInput
                      label="Remarks"
                      isRequired
                      type="textarea"
                      value={formData?.remarks}
                      onChange={(val) => setField("remarks", val)}
                      placeholder="Write Details about Assessment or Diagnosis..."
                      rows={4}
                    />
                  </div>

                </FormSection>

                <FormSection title="Attachments">
                  <MultiDocumentSelect
                    attachment_base_url={lab_report_attachments_url}
                    label="Investigations / Lab Results"
                    files={attachments}
                    onChange={handleAttachmentsChange}
                  />
                </FormSection>
              </div></FormSection>
            <FormSection title="Status">
              <div className="flex gap-3 items-center">
                <FormSwitch checked={formData.status} onChange={(val) => setField("status", Boolean(val))} />
              </div>
            </FormSection>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  navigate(ROUTE_PATHS.LAB_REPORT_LIST);
                }}
                className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="!bg-green-500 !text-white hover:!bg-green-600"
                loading={loading}
                disabled={loading}
                onClick={onSubmit}
                loadingText={patientLabReportDetails ? "Updating..." : "Creating..."}
              >
                {patientLabReportDetails ? "Update Changes" : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>

        <div className="xl:col-span-1">
          <PatientProfile patientDetails={patientDetails} />
        </div>
      </div>
    </div>
  );
};

export default CreateLabReports;
